import { RISK_COLORS, RISK_HEADER_KEYS, RISK_STATE_CONFIG, RegulatoryRiskState } from '../../../RiskIndicators/RiskIndicatorConstants';
import { hasLegalCasesCountsShape, resolveLegalCasesState } from '../../../RiskIndicators/legalCasesRiskState';
import type { LegalCasesRiskState } from '../../../RiskIndicators/RiskIndicators';
import type { Direction } from '../../components/DashboardTables/LegalCases/legalCasesType';
import { LEGAL_DIRECTION } from '../../components/DashboardTables/LegalCases/legalCasesConstants';
import { ALERT_UNITS, INDICATOR_UNITS, RISK_ALERTS_STATE } from './ManagementProfileTabConstants';
import type { DirectorTableRow } from './useManagementProfileTabLogic';
import type { DirectorLegalCases, NotConfiguredCheckKey, RegulatoryBadge, RegulatoryCheckDisplay, RiskAlertsCellState } from './types';

// Pure state decisions shared by the Management Profile table cells and the director drawer rows,
// so the two surfaces resolve every check through one function each.

/**
 * The Risk Alerts state for a row. polus sends riskAlertsState; until it does, the count alone
 * decides, exactly as before (a count of alerts, or nothing to show).
 */
export function resolveRiskAlertsState(row: Pick<DirectorTableRow, 'riskAlertsState' | 'riskIndicatorCount'>): RiskAlertsCellState {
    if (row.riskAlertsState !== undefined) {
        // ALERTS without a positive count would read "0 Alerts" and click through to nothing.
        if (row.riskAlertsState === RISK_ALERTS_STATE.ALERTS && !((row.riskIndicatorCount ?? 0) > 0)) return null;
        return row.riskAlertsState;
    }
    const count = row.riskIndicatorCount;
    if (count == null) return null;
    return count > 0 ? RISK_ALERTS_STATE.ALERTS : RISK_ALERTS_STATE.NO_ALERTS;
}

/*
    RECUR-27829. The Regulatory Checks badge decision, as a pure function.

    Extracted rather than inlined in the cell so it can be asserted directly, and so the badge's
    rule is demonstrably the SAME one the Risk Indicators cell applies — show the confirmed count
    when there is one, otherwise the probable count. RCPI-10463 was opened because these two
    surfaces disagreed; keeping the rule in one testable place is what stops that recurring.
*/
export function resolveRegulatoryBadge(row: {
    amlCount?: {
        confirmed?: number | null;
        probable?: number | null;
        flaggedCategories?: number | null;
    };
}): RegulatoryBadge {
    const { confirmed, probable, flaggedCategories: legacy } = row.amlCount ?? {};

    // Both null => UW has not shipped. Fall back to the legacy count rather than rendering
    // "No Indicator", which would assert a clean result nobody computed.
    if (confirmed == null && probable == null) {
        return {
            count: legacy ?? null,
            label: legacy === 1 ? INDICATOR_UNITS.SINGULAR : INDICATOR_UNITS.PLURAL,
            color: RISK_COLORS.CONFIRMED.color,
            backgroundColor: RISK_COLORS.CONFIRMED.backgroundColor,
            isFallback: true,
        };
    }

    // Confirmed-first, the same precedence the Risk Indicators cell applies. The counts are hit
    // unions, so this is the number the table shows for that entity, not a count of its cells.
    const isConfirmed = (confirmed ?? 0) > 0;
    return regulatoryBadgeFor(isConfirmed ? confirmed : probable, isConfirmed);
}

/** The confirmed-first badge: the count in the Confirmed or Probable colours, labelled by that state. */
function regulatoryBadgeFor(count: number | null | undefined, isConfirmed: boolean): RegulatoryBadge {
    const state = isConfirmed ? RegulatoryRiskState.CONFIRMED : RegulatoryRiskState.PROBABLE;
    const palette = isConfirmed ? RISK_COLORS.CONFIRMED : RISK_COLORS.PROBABLE;
    return {
        count,
        label: RISK_STATE_CONFIG[state].word ?? '',
        color: palette.color,
        backgroundColor: palette.backgroundColor,
        isFallback: false,
    };
}

/** "1 Alert" / "N Alerts", shared by the Risk Alerts cell and drawer row. */
export const formatAlertCount = (count: number): string => `${count} ${count === 1 ? ALERT_UNITS.SINGULAR : ALERT_UNITS.PLURAL}`;

/** The red "N Alerts" badge the drawer row draws, in the same shape as the regulatory badge. */
export function riskAlertsBadgeFor(count: number): RegulatoryBadge {
    return {
        count,
        label: count === 1 ? ALERT_UNITS.SINGULAR : ALERT_UNITS.PLURAL,
        color: RISK_COLORS.CONFIRMED.color,
        backgroundColor: RISK_COLORS.CONFIRMED.backgroundColor,
        isFallback: false,
    };
}

/** Lock and loading still render the dash; only then does the PNO's config decide Not Configured. */
export function isCheckNotConfigured(row: DirectorTableRow, check: NotConfiguredCheckKey, locked = false): boolean {
    return !locked && !row.loading && !!row.notConfiguredChecks?.[check];
}

/**
 * What the Regulatory Checks cell (and the drawer row) shows. polus sends `regulatoryCheck`: the
 * director's own Legal & Regulatory row rolled up, so this reads the same state and number as that
 * table. A polus without it falls back to the amlCount badge, as before.
 */
export function resolveRegulatoryCheck(row: Pick<DirectorTableRow, 'regulatoryCheck' | 'amlCount'>): RegulatoryCheckDisplay {
    const check = row.regulatoryCheck;
    if (check === undefined) {
        const badge = resolveRegulatoryBadge(row);
        if (badge.count == null) return { kind: 'dash' };
        return badge.count > 0 ? { kind: 'badge', badge } : { kind: 'noMatch' };
    }
    // null = polus found no Legal & Regulatory row for this director. That is a dash on purpose: the
    // legacy amlCount fallback is only for a polus that does not send regulatoryCheck at all.
    switch (check?.state) {
        case RegulatoryRiskState.CONFIRMED:
            return { kind: 'badge', badge: regulatoryBadgeFor(check.confirmedCount ?? 0, true) };
        case RegulatoryRiskState.PROBABLE:
            return { kind: 'badge', badge: regulatoryBadgeFor(check.probableCount ?? 0, false) };
        case RegulatoryRiskState.NO_RISK:
            return { kind: 'noMatch' };
        case RegulatoryRiskState.NOT_AVAILABLE:
            return { kind: 'noData' };
        case RegulatoryRiskState.NOT_CHECKED:
            return { kind: 'notChecked' };
        default:
            return { kind: 'dash' };
    }
}

const LEGAL_CASES_KEY_BY_DIRECTION: Record<Direction, keyof DirectorLegalCases> = {
    [LEGAL_DIRECTION.FILED_AGAINST]: RISK_HEADER_KEYS.LEGAL_CASES_KEY,
    [LEGAL_DIRECTION.FILED_BY]: RISK_HEADER_KEYS.LEGAL_CASES_BY_KEY,
};

/**
 * A director's Legal Cases state in one direction, from the same cell and the same resolver as the
 * Legal & Regulatory table. null when polus sent no cell for that direction.
 */
export function resolveDirectorLegalCases(row: Pick<DirectorTableRow, 'legalCases'>, direction: Direction): LegalCasesRiskState | null {
    const cell = row.legalCases?.[LEGAL_CASES_KEY_BY_DIRECTION[direction]];
    return cell && hasLegalCasesCountsShape(cell) ? resolveLegalCasesState(cell, direction) : null;
}
